import React, { useEffect, useMemo, useState } from "react";
import styles from "./ModalNewStaff.module.scss";
import Input from "../../components/Input/Input";
import Modal from "../../components/Modal/Modal";
import ImageLoader from "../../components/ImageLoader/ImageLoader";
import { ROLES } from "../../utils/constants";
import { isValidEmail } from "../../utils/util";
import { useDispatch, useSelector } from "react-redux";
import {
  editStudent,
  signUpStudent,
} from "../../store/slices/student/asyncThunks";
import {
  selectEditStudentPending,
  selectSignUpStudentPending,
} from "../../store/slices/student/slice";
import { notifyError, notifyInfo } from "../../utils/notify";
import { editStaff, signUpStaff } from "../../store/slices/staff/asyncThunks";
import Button from "../Button/Button";
import classNames from "classnames";
import {
  selectEditStaffPending,
  selectSignUpStaffPending,
} from "../../store/slices/staff/slice";
import { selectLoggedInUserRole } from "../../store/slices/user/slice";

const ModalNewStaff = ({
  modalRef,
  staffType,
  selectedStudent,
  selectedStaff,
}) => {
  const dispatch = useDispatch();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [externalId, setExternalId] = useState("");
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [graduationYear, setGraduationYear] = useState("");
  const [role, setRole] = useState(ROLES.TEACHER);
  const signUpStudentPending = useSelector(selectSignUpStudentPending);
  const editStudentPending = useSelector(selectEditStudentPending);
  const editStaffPending = useSelector(selectEditStaffPending);
  const signUpStaffPending = useSelector(selectSignUpStaffPending);
  const userRole = useSelector(selectLoggedInUserRole);

  useEffect(() => {
    if (selectedStudent) {
      setFirstName(selectedStudent.firstName);
      setLastName(selectedStudent.lastName);
      setEmail(selectedStudent.email);
      if (selectedStudent.externalId) {
        setExternalId(selectedStudent.externalId);
      } else {
        setExternalId("");
      }
      setGraduationYear(selectedStudent.graduationYear + "");
    } else {
      resetForm();
    }
  }, [selectedStudent]);

  useEffect(() => {
    if (selectedStaff) {
      setFirstName(selectedStaff.firstName);
      setLastName(selectedStaff.lastName);
      setEmail(selectedStaff.email);
      if (selectedStaff.externalId) {
        setExternalId(selectedStaff.externalId);
      } else {
        setExternalId("");
      }
      setRole(selectedStaff.role);
    } else {
      resetForm();
    }
  }, [selectedStaff]);

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setExternalId("");
    setGraduationYear("");
    setProfilePhoto(null);
    setRole(ROLES.TEACHER);
  };

  const confirmAction = () => {
    if (staffType === ROLES.STUDENT) {
      if (selectedStudent) {
        dispatch(
          editStudent({
            id: selectedStudent.id,
            firstName,
            lastName,
            email,
            studentId: externalId,
            graduationYear,
            imageFile: profilePhoto,
          })
        ).then((promise) => {
          if (promise.type.includes("fulfilled")) {
            notifyInfo("Successfully edited");
            modalRef.current.hide();
          } else {
            notifyError(promise.error.message);
          }
        });
      } else {
        dispatch(
          signUpStudent({
            firstName,
            lastName,
            email,
            studentId: externalId,
            graduationYear,
            imageFile: profilePhoto,
          })
        ).then((promise) => {
          if (promise.type.includes("fulfilled")) {
            notifyInfo("Successfully registered");
            modalRef.current.hide();
            resetForm();
          } else {
            notifyError(promise.error.message);
          }
        });
      }
    } else {
      if (selectedStaff) {
        dispatch(
          editStaff({
            id: selectedStaff.id,
            firstName,
            lastName,
            email,
            externalId,
            role,
            imageFile: profilePhoto,
          })
        ).then((promise) => {
          if (promise.type.includes("fulfilled")) {
            notifyInfo("Successfully edited");
            modalRef.current.hide();
          } else {
            notifyError(promise.error.message);
          }
        });
      } else {
        dispatch(
          signUpStaff({
            firstName,
            lastName,
            email,
            externalId,
            role,
            imageFile: profilePhoto,
          })
        ).then((promise) => {
          if (promise.type.includes("fulfilled")) {
            notifyInfo("Successfully registered");
            modalRef.current.hide();
            resetForm();
          } else {
            notifyError(promise.error.message);
          }
        });
      }
    }
  };

  const modalTitle = useMemo(() => {
    switch (staffType) {
      case ROLES.ADMIN:
        return "New Staff";
      case ROLES.STUDENT:
        return "New Student";
      default:
        return "";
    }
  }, [staffType]);

  const btnDisabled = useMemo(() => {
    switch (staffType) {
      case ROLES.ADMIN:
        return (
          !firstName?.trim() ||
          !lastName?.trim() ||
          !isValidEmail(email) ||
          !externalId?.trim()
        );
      case ROLES.STUDENT:
        return (
          !firstName?.trim() ||
          !lastName?.trim() ||
          !isValidEmail(email) ||
          !externalId?.trim() ||
          !graduationYear?.trim()
        );
      default:
        return true;
    }
  }, [staffType, firstName, lastName, email, externalId, graduationYear]);

  return (
    <Modal
      ref={modalRef}
      title={modalTitle}
      buttonOkText={selectedStudent ? "EDIT" : "CONFIRM"}
      buttonCloseText="CANCEL"
      buttonOkOnClick={confirmAction}
      buttonCloseOnClick={() => {
        modalRef.current.hide();
        resetForm();
      }}
      showOkButton={userRole === ROLES.ADMIN}
      pendingAction={
        signUpStudentPending ||
        editStudentPending ||
        editStaffPending ||
        signUpStaffPending
      }
      onClose={() => modalRef.current.hide()}
      buttonOkDisabled={btnDisabled}
    >
      <div style={{ flexDirection: "column", gap: 20, display: "flex" }}>
        <ImageLoader
          textUploadImage={"Upload Photo"}
          flexColumn={false}
          user={staffType === ROLES.ADMIN ? selectedStaff : selectedStudent}
          setImageFile={setProfilePhoto}
          classNameContainer={styles.imageLoader}
          onlyPreview={userRole === ROLES.TEACHER}
        />
        <Input
          placeholder="First Name"
          value={firstName}
          onChange={(event) => setFirstName(event)}
          disabled={userRole === ROLES.TEACHER}
        />
        <Input
          placeholder="Last Name"
          value={lastName}
          onChange={(event) => setLastName(event)}
          disabled={userRole === ROLES.TEACHER}
        />
        <Input
          placeholder="Email"
          value={email}
          onChange={(event) => setEmail(event)}
          disabled={userRole === ROLES.TEACHER}
        />
        <Input
          placeholder="External ID"
          value={externalId}
          onChange={(event) => setExternalId(event)}
          disabled={userRole === ROLES.TEACHER}
        />
        {staffType === ROLES.ADMIN && (
          <div className={styles.roleButtonsContainer}>
            <Button
              title={ROLES.ADMIN.toUpperCase()}
              onClick={() => setRole(ROLES.ADMIN)}
              className={classNames(
                styles.roleBtn,
                role === ROLES.ADMIN && styles.roleBtnSelected
              )}
            />
            <Button
              title={ROLES.TEACHER.toUpperCase()}
              onClick={() => setRole(ROLES.TEACHER)}
              className={classNames(
                styles.roleBtn,
                role === ROLES.TEACHER && styles.roleBtnSelected
              )}
            />
          </div>
        )}

        {staffType === ROLES.STUDENT && (
          <Input
            placeholder="Graduation Year"
            value={graduationYear}
            onChange={(event) => setGraduationYear(event)}
            disabled={userRole === ROLES.TEACHER}
          />
        )}
      </div>
    </Modal>
  );
};

export default ModalNewStaff;
